import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { UserProfileService } from '../services/user-profile.service';
import { APP_ROUTES } from 'src/app/shared/enums/appRoutes.enum';

/**
 * MVP-15 — Super User route guard.
 *
 * Gates `/su/*` routes. Uses `getUserProfile()` (not `getProfileObservable()`)
 * so the guard triggers an HTTP fetch on hard refresh when the in-memory
 * BehaviorSubject hasn't been primed yet — otherwise the guard would hang on
 * the initial `null` seed and leave the user staring at a blank page.
 *
 * When `isSuperUser !== true`, we redirect to HOME rather than LOGIN — a
 * non-SU authenticated user is not unauthenticated, they're simply not
 * entitled to the SU area. On profile-fetch error we also redirect HOME
 * (fail-closed).
 */
export const superUserGuard: CanActivateFn = (): Observable<boolean> => {
  const userProfileService = inject(UserProfileService);
  const router = inject(Router);

  return userProfileService.getUserProfile().pipe(
    take(1),
    map(profile => {
      if (profile?.isSuperUser === true) {
        return true;
      }
      router.navigate([APP_ROUTES.HOME]);
      return false;
    }),
    catchError(() => {
      router.navigate([APP_ROUTES.HOME]);
      return of(false);
    })
  );
};
